import {
	getCart,
	getProduct,
	getRecommendedProducts,
	getMetafields,
	cartOperationManager
} from './_global-functions.js';

// Function to ensure Swiper is loaded
async function ensureSwiperLoaded() {
	if (window.Swiper) {
		return Promise.resolve();
	}
	
	// Check if script already exists (look for any swiper script)
	const existingScript = document.querySelector('script[src*="swiper"]');
	
	if (existingScript) {
		// Wait for it to load
		return new Promise((resolve) => {
			const checkSwiper = setInterval(() => {
				if (window.Swiper) {
					clearInterval(checkSwiper);
					resolve();
				}
			}, 50);
			
			// Timeout after 5 seconds
			setTimeout(() => {
				if (!window.Swiper) {
					clearInterval(checkSwiper);
					loadSwiperFromCDN().then(resolve);
				}
			}, 5000);
		});
	}
	
	// Load from CDN
	return loadSwiperFromCDN();
}

function loadSwiperFromCDN() {
	return new Promise((resolve, reject) => {
		// Check if CDN script already exists
		if (document.querySelector('script[src*="cdn.jsdelivr.net/npm/swiper"]')) {
			const checkSwiper = setInterval(() => {
				if (window.Swiper) {
					clearInterval(checkSwiper);
					resolve();
				}
			}, 50);
			return;
		}
		
		const script = document.createElement('script');
		script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
		script.onload = () => {
			resolve();
		};
		script.onerror = (error) => {
			console.error('Failed to load Swiper from CDN:', error);
			reject(error);
		};
		document.head.appendChild(script);
	});
}

class CartSlider extends HTMLElement {
	constructor() {
		super();

		this.sliderProducts;
		this.options;
		this.swiper;
		this.isReloading = false;
		this.cart;
		this.cartItemsMetafields;
		this.lastRequestedData = null; // Track last data we requested
		this.isInitialized = false; // Prevent duplicate initialization
		this.lastCartItemCount = 0; // Track cart item count
		this.batchReloadInProgress = false; // Track if we're in a batch reload
	}

	async connectedCallback() {
		// Only initialize once
		if (this.isInitialized) {
			return;
		}
		
		this.isInitialized = true;
		
		// Set up event listeners first (before initSlider)
		this.setupEventListeners();
		
		// Then initialize the slider
		await this.initSlider();
	}

	hideSlider() {
		const sliderContainer = this.closest('.mini-cart--slider');
		if (sliderContainer) {
			sliderContainer.style.transition = 'opacity 0.3s ease';
			sliderContainer.style.opacity = '0';
			
			// Hide after transition completes
			setTimeout(() => {
				this.style.display = 'none';
				sliderContainer.style.display = 'none';
			}, 300);
		} else {
			this.style.display = 'none';
		}
	}



	// Create a stable hash from metafields (ignoring dynamic/session data)
	createStableMetafieldsHash(metafields) {
		if (!metafields) return '';
		
		const stableData = {};
		Object.keys(metafields).forEach(productHandle => {
			const productMetafields = metafields[productHandle];
			stableData[productHandle] = {};
			
			// Only include stable metafield values (exclude timestamps, session data, etc.)
			Object.keys(productMetafields).forEach(key => {
				const metafield = productMetafields[key];
				// Focus on the actual value, not metadata that might change
				if (metafield && metafield.value) {
					stableData[productHandle][key] = metafield.value;
				}
			});
		});
		
		return JSON.stringify(stableData);
	}

	// Create a hash from the data to compare changes
	createDataHash(cart, sliderProducts, metafields) {
		const cartHash = cart?.items?.map(item => `${item.id}-${item.quantity}`).sort().join('|') || '';
		const productsHash = sliderProducts?.map(product => `${product.id}-${product.handle}`).sort().join('|') || '';
		const metafieldsHash = this.createStableMetafieldsHash(metafields);
		const finalHash = `${cartHash}:${productsHash}:${metafieldsHash}`;

		
		return finalHash;
	}

	// Compare new data with last requested data
	hasDataChanged(newCart, newSliderProducts, newMetafields) {
		// For slider purposes, we only care about:
		// 1. Different products in the slider (handles/IDs)
		// 2. Different metafields for those products
		// We DON'T care about cart item quantities, prices, or other cart details
		
		const newSliderDataHash = this.createSliderDataHash(newSliderProducts, newMetafields);
		
		// Compare with last requested data
		const hasChanged = this.lastRequestedData !== newSliderDataHash;
		
		// Update the reference for next comparison
		this.lastRequestedData = newSliderDataHash;
		return hasChanged;
	}

	// Create hash only for slider-relevant data (products and their metafields)
	createSliderDataHash(sliderProducts, metafields) {
		const shouldChange = true;

		

		const sliderData = {
			productHandles: sliderProducts?.map(p => p.handle).sort() || [],
			metafieldsHash: metafields ? JSON.stringify(metafields) : 'false',
			shouldChange: shouldChange
		};
		
		return JSON.stringify(sliderData);
	}

	// Check if we should allow a reload based on cart changes
	async shouldAllowReload() {
		try {
			const currentCart = await getCart();
			const currentItemCount = currentCart?.item_count || 0;
			const itemCountIncrease = currentItemCount - this.lastCartItemCount;
			
			
			// If we're already in a batch reload, don't allow another one
			if (this.batchReloadInProgress) {
				return false;
			}
			
			// If multiple items were added (increase > 1), start a batch reload
			if (itemCountIncrease > 1) {
				this.batchReloadInProgress = true;
				
				// Set a timeout to end the batch reload after 2 seconds
				setTimeout(() => {
					this.batchReloadInProgress = false;
					this.lastCartItemCount = currentItemCount;
				}, 2000);
				
				return true; // Allow this reload
			}
			
			// If only 1 item was added, allow the reload
			if (itemCountIncrease === 1) {
				this.lastCartItemCount = currentItemCount;
				return true;
			}
			
			// If no items were added or items were removed, don't reload
			return false;
			
		} catch (error) {
			console.error('Error checking if reload should be allowed:', error);
			return true; // Default to allowing reload on error
		}
	}


	async reloadSlider() {
		// Prevent multiple simultaneous reloads
		if (this.isReloading) {
			return;
		}
		
		this.isReloading = true;
		
		try {
			// STEP 1: Get new data first
			const newCart = await getCart();
			
			if (!newCart || !newCart.items.length) {
				console.error("No cart or cart items found for cart slider reload");
				this.hideSlider();
				return;
			}

			// Update cart reference for getSliderData to work
			this.cart = newCart;
			
			// Get new slider products
			const newSliderProducts = await this.getSliderData();
			
			if(!newSliderProducts.length) {
				console.error("No products found for cart slider reload");
				this.hideSlider();
				return;
			}

			// Temporarily update sliderProducts for metafields call
			const originalSliderProducts = this.sliderProducts;
			this.sliderProducts = newSliderProducts;
			
			// Get new metafields
			const newMetafields = await this.getSliderProductMetafields() ?? false;
			
			// Restore original sliderProducts
			this.sliderProducts = originalSliderProducts;

			// STEP 2: Compare with last requested data
			if (!this.hasDataChanged(newCart, newSliderProducts, newMetafields)) {
				// Data hasn't changed, no need to reload slider
				return;
			}

			// STEP 3: Update the slider data
			this.cart = newCart;
			this.sliderProducts = newSliderProducts;
			this.cartItemsMetafields = newMetafields;
			
			// STEP 4: Smart slide updates using Swiper API
			if (this.swiper) {
				await this.updateSlidesSmartly();
			} else {
				// First time initialization
				await this.initializeSwiper();
			}
			
		} catch (error) {
			console.error('Error in reloadSlider:', error);
			this.hideSlider();
		} finally {
			// Allow subsequent reloads after a brief delay
			setTimeout(() => {
				this.isReloading = false;
			}, 300);
		}
	}

	// Smart slide update - add new slides first, then remove old ones if needed
	async updateSlidesSmartly() {
		if (!this.swiper || !this.sliderProducts?.length) {
			return;
		}

		// Create new slide HTML
		const newSlides = this.createSlideElements();
		
		if (!newSlides?.length) {
			console.warn('No new slides to add');
			return;
		}

		// Get current number of slides
		const currentSlideCount = this.swiper.slides.length;
		
		// Add new slides first (at the end)
		this.swiper.appendSlide(newSlides);
		
		// If we had old slides, remove them (they're at the beginning)
		if (currentSlideCount > 0) {
			// Remove old slides (indices 0 to currentSlideCount-1)
			const indicesToRemove = Array.from({length: currentSlideCount}, (_, i) => i);
			this.swiper.removeSlide(indicesToRemove);
		}
		
		// Go to first slide of new content
		this.swiper.slideTo(0, 0); // 0 speed for instant transition
	}


	async initSlider() {
		try {
			// Get cart first
			this.cart = await getCart();
			
			if (!this.cart || !this.cart.items.length) {
				console.warn(`No cart or cart items found for cart slider initialization`);
				// Don't hide slider, just wait for reload event
				return;
			}
			
			
			// Initialize cart item count for change tracking
			this.lastCartItemCount = this.cart.item_count || 0;
			
			// Get slider products first, then get metafields for THOSE products
			this.sliderProducts = await this.getSliderData();
			
			if(!this.sliderProducts.length) {
				console.error("No products found for cart slider");
				this.hideSlider();
				return;
			};
			
			// Get metafields for the slider products (not cart items)
			this.cartItemsMetafields = await this.getSliderProductMetafields() ?? false;

			// Initialize the data hash for future comparisons (slider-specific)
			this.lastRequestedData = this.createSliderDataHash(this.sliderProducts, this.cartItemsMetafields);
			
			// Initialize the swiper
			await this.initializeSwiper();
			
		} catch (error) {
			console.error('Error in initSlider:', error);
			this.hideSlider();
		}
	}

	// Initialize Swiper instance
	async initializeSwiper() {
		// Ensure Swiper is loaded before initializing
		await ensureSwiperLoaded();
		
		if (!window.Swiper) {
			console.error('Swiper still not available after loading attempt');
			this.hideSlider();
			return;
		}
		
		this.options = JSON.parse(this.getAttribute('data-options'));
		
		const slider = this.querySelector('.swiper');
		
		// Create initial slides
		const slides = this.createSlideElements();
		const wrapper = slider.querySelector('.swiper-wrapper');
		wrapper.innerHTML = slides.join('');
		
		// Initialize Swiper
		this.swiper = new window.Swiper(slider, this.options);
	}

	// Create slide HTML elements (returns array of HTML strings)
	createSlideElements() {
		// Filter out products with price = 0 (free products)
		const filteredProducts = this.sliderProducts.filter(product => {
			const productData = this.getProductDataForSlide(product);
			const price = parseFloat(productData.price) || 0;
			const isFree = price === 0;
			
			return !isFree; // Only include non-free products
		});

		return filteredProducts.map(product => {
			// Get metafields for this product handle if available
			const productMetafields = this.cartItemsMetafields.data.filter(metafield => metafield.handle === product.handle)[0];

			// Get product data from metafields or use defaults
			const productData = this.getProductDataForSlide(product);

			return `
				<div class="swiper-slide">
					<product-card 
						data-product-handle='${product.handle}'
						data-product-url='${productData.url}'
						data-product-title='${productData.title}'
						data-product-subtitle='${productData.subtitle}'
						data-product-price='${productData.price}'
						data-product-image='${productData.image}'
					></product-card>
				</div>
			`;
		});
	}

	setupEventListeners() {
		document.addEventListener('add-to-cart-slider', async (e) => {
			if(e.detail.status === 'empty') {
				this.hideSlider();
				return;
			}

			if(e.detail.status === 'reload') {
				// Check if we should allow this reload
				const shouldReload = await this.shouldAllowReload();
				
				if (!shouldReload) {
					return;
				}
				
				// If slider was never initialized (no swiper instance), call initSlider instead
				if (!this.swiper) {
					await this.initSlider();
				} else {
					await this.reloadSlider();
				}
			}
		});

	}

	createSlides(slider) {
		// Filter out products with price = 0 (free products)
		const filteredProducts = this.sliderProducts.filter(product => {
			const productData = this.getProductDataForSlide(product);
			const price = parseFloat(productData.price) || 0;
			const isFree = price === 0;

			return !isFree; // Only include non-free products
		});

		const slides = filteredProducts.map(product => {
			// Get metafields for this product handle if available
			const productMetafields = this.cartItemsMetafields && this.cartItemsMetafields[product.handle] 
				? JSON.stringify(this.cartItemsMetafields[product.handle]) 
				: '';

			// Get product data from metafields or use defaults
			const productData = this.getProductDataForSlide(product);

			return `
				<div class="swiper-slide">
					<product-card 
						data-product-handle='${product.handle}'
						data-product-url='${productData.url}'
						data-product-title='${productData.title.replace(/'/g, '&apos;')}'
						data-product-subtitle='${productData.subtitle.replace(/'/g, '&apos;')}'
						data-product-price='${productData.price}'
						data-product-image='${productData.image}'
						${productMetafields ? `data-metafields='${productMetafields.replace(/'/g, '&apos;')}'` : ''}
					></product-card>
				</div>
			`;
		});

		slider.querySelector('.swiper-wrapper').innerHTML = slides.join('');
	}

	getProductDataForSlide(product) {
		// Check if we have metafields for this product
		const productMetafields = this.cartItemsMetafields && this.cartItemsMetafields.data.filter(metafield => metafield.handle === product.handle)[0];
		
		// Get URL (check for cart redirect first)
		let url = product.url;
		let cartRedirect = this.cartItemsMetafields.get('cart.cart_redirect', null, String(product.id))
		if (productMetafields && cartRedirect) {
			url = cartRedirect;
		}

		// Get title (check for custom title first)
		let title = product.title;
		let cartTitle = this.cartItemsMetafields.get('cart.title', null, String(product.id))
		if (productMetafields && cartTitle) {
			title = cartTitle;
		}

		// Get subtitle (check for custom subtitle first)
		let subtitle = product.type || '';
		let cartSubtitle = this.cartItemsMetafields.get('cart.subtitle', null, String(product.id))
		if (productMetafields && cartSubtitle) {
			subtitle = cartSubtitle;
		}

		// Get image (check for main image metafield first)
		let image = product.featured_image;
		let cartRolloverImage = JSON.parse(this.cartItemsMetafields.get('rollover.rollover_image', null, String(product.id)))?.[0]
		if (productMetafields && cartRolloverImage) {
			image = cartRolloverImage.src;
		}

		let price = product.price;
		if (price === 0) {
			const variantPrices = product.variants.filter(variant => variant.price > 0)
			if (variantPrices.length > 0) {
				price = variantPrices[0].price;
			}
		}

		return {
			url: url,
			title: title,
			subtitle: subtitle,
			price: price,
			image: image
		};
	}

	async createSlidesWithImagePreload(slider) {
		// Create slides first
		this.createSlides(slider);
		
		
		// Wait for product cards to load and preload their images
		const productCards = slider.querySelectorAll('product-card');
		const imageLoadPromises = [];
		
		productCards.forEach(card => {
			// Wait for the product card to initialize
			const checkForImages = () => {
				const images = card.querySelectorAll('img');
				images.forEach(img => {
					if (img.src && !img.complete) {
						imageLoadPromises.push(new Promise((resolve) => {
							img.onload = resolve;
							img.onerror = resolve; // Don't block on failed images
							// Timeout after 2 seconds to avoid blocking indefinitely
							setTimeout(resolve, 2000);
						}));
					}
				});
			};
			
			// Check immediately and also after a brief delay for dynamic content
			checkForImages();
			setTimeout(checkForImages, 100);
		});
		
		// Wait for all images to load or timeout
		if (imageLoadPromises.length > 0) {
			await Promise.all(imageLoadPromises);
		}
	}

	async getSliderProductMetafields(type = 'product', metafieldNames = null) {
		// Validate slider products exist before proceeding
		if (!this.sliderProducts || !Array.isArray(this.sliderProducts) || this.sliderProducts.length === 0) {
			console.warn('Slider products not available for metafields fetch:', {
				sliderProducts: this.sliderProducts
			});
			return false;
		}

		if(metafieldNames === null) {
			metafieldNames = this.dataset.metafields.split(',');
		}

		// Get metafields for the slider products, not cart products
		const productIds = this.sliderProducts.map(product => product.id);
		const metafields = await getMetafields(type, productIds, metafieldNames);
		return metafields;
	}

	async getSliderData() {
		const selectedProduct = await this.selectByPriorityTag();
		
		if (!selectedProduct) {
			console.error('No selected product found for slider data');
			return [];
		}

		const id = selectedProduct.id;
		
		// Try complementary recommendations first
		let recommendedProducts = await getRecommendedProducts(id, 'complementary');

		// If no complementary products, try related
		if (!recommendedProducts.products || recommendedProducts.products.length === 0) {
			recommendedProducts = await getRecommendedProducts(id, 'related');
		}

		// If still no products, try with other products from cart as fallback
		if (!recommendedProducts.products || recommendedProducts.products.length === 0) {			
			// Get other products from cart (excluding the current priority product)
			const otherCartProducts = this.cart.items.filter(item => item.product_id !== id);
			
			for (const cartItem of otherCartProducts) {
				recommendedProducts = await getRecommendedProducts(cartItem.product_id, 'complementary');
				
				if (recommendedProducts.products && recommendedProducts.products.length > 0) {
					break;
				}
			}
		}

		// Ensure we always return an array
		return recommendedProducts.products || [];
	}

	// Helper function to get the current priority product from existing slider data
	async getCurrentPriorityProduct() {
		if (!this.sliderProducts || this.sliderProducts.length === 0) {
			return null;
		}
		
		// We need to determine what the original priority product was
		// Since we don't store this directly, we'll use the current cart to determine it
		if (!this.cart || !this.cart.items) {
			return null;
		}
		
		// Use the same logic as getPriorityProductFromCart to find the current priority product
		return await this.getPriorityProductFromCart(this.cart);
	}

	// Helper function to get priority product from a given cart
	async getPriorityProductFromCart(cart) {
		if (!cart || !cart.items || cart.items.length === 0) {
			return null;
		}
		
		const cartItems = cart.items;
		const cartItemsHandles = cartItems.map(item => item.handle);

		const products = await Promise.all(cartItemsHandles.map(handle => getProduct(handle)));
		
		// Filter out null products (failed fetches)
		const validProducts = products.filter(product => product && product.tags);
		
		if (validProducts.length === 0) {
			console.warn('No valid products found for priority selection');
			return null;
		}
		
		let selectedProduct = validProducts.find(product => product.tags.includes('Upsell-Promo'));

		// If no product is found with the highest priority tag, continue with normal flow
		if(!selectedProduct) {
			// for loop from 1 to 5
			for(let i = 1; i <= 5; i++) {
				selectedProduct = validProducts.find(product => product.tags.includes(`Upsell-P${i}`));
				
				if(selectedProduct) {
					break;
				}
			}
		}

		if(!selectedProduct) {
			selectedProduct = validProducts[0]; // Use first valid product as fallback
		}

		return selectedProduct;
	}

	async selectByPriorityTag() {
		// Use stored cart instead of fetching again
		if(!this.cart) {
			console.error("No cart found");
			return;
		};
		
		const selectedProduct = await this.getPriorityProductFromCart(this.cart);
		return selectedProduct;
	}

	disconnectedCallback() {
		
		// Clean up swiper instance when element is removed
		if (this.swiper) {
			try {
				this.swiper.destroy(true, true);
				this.swiper = null;
			} catch (error) {
				console.warn('Error destroying swiper in cleanup:', error);
			}
		}
	}
}

customElements.define('cart-slider', CartSlider);